import { useEffect, useState } from 'react';

import type { UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';
import { computeTier, formatTrialAllowanceRow } from '@flintmint/core-shared/utils';
import { Button } from '@flintmint/web-shared/components/button';
import { Loader } from '@flintmint/web-shared/components/loader';

import { useAppSettings } from '@/hooks/use-app-settings';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { urlOverlays } from '@/constants/url-overlays';
import { usePlans } from '@/hooks/use-plans';
import { useUserAccount } from '@/hooks/use-user-account';
import { FullScreenOverlay, FullScreenOverlayPresence, OverlayNote, OverlayPanel } from '@/components/full-screen-overlay';

import { formatBillingDate, formatMonthlyEnergyRow, formatPrice, intervalLabels } from '../billing-format';
import { findSubscriptionPlan, isPendingFirstCharge } from '../billing-state';
import { type SkeletonLineStyle, Text, TextSkeleton, type TextStyle } from '../billing-text';

import styles from './checkout-success.module.scss';

/** How long the wait for the subscription sync lasts before the overlay says it is taking longer. */
export const TAKING_LONGER_MS = 30_000;

/** Mounted on Subscription & Billing; Checkout's success URL opens it with `?checkout=success`. */
export const CheckoutSuccess = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.checkoutSuccess, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <CheckoutSuccessGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/**
 * A comped account never goes through Checkout, so it closes the overlay unseen. So does a first charge that
 * failed or needs authentication (an `incomplete` or never-paid `past_due` subscription): no subscription will
 * arrive to wait for, and the page under it shows the pending payment.
 */
const CheckoutSuccessGate = ({ onClose }: { onClose: () => void }) => {
  const { account, loading } = useUserAccount();
  const stepAside = !loading && (computeTier(account) === 'comped' || isPendingFirstCharge(account?.stripeSubscription));

  useEffect(() => {
    if (stepAside) onClose();
  }, [stepAside, onClose]);

  return !stepAside && <CheckoutSuccessDialog account={account} accountLoading={loading} onClose={onClose} />;
};

type CheckoutSuccessDialogProps = { account: UserAccountDoc | undefined; accountLoading: boolean; onClose: () => void };

/**
 * The account reads Free until the subscription sync stores the new subscription, usually within seconds of
 * the return; the live account then turns the wait into the Trial or paid confirmation in place. It never
 * offers plans or Checkout. While the account loads, the waiting layout renders with skeleton texts.
 */
const CheckoutSuccessDialog = ({ account, accountLoading, onClose }: CheckoutSuccessDialogProps) => {
  const subscription = !accountLoading && computeTier(account) === 'paid' ? account?.stripeSubscription : undefined;
  // Requested while it waits, so the plans are usually in hand when the subscription arrives.
  const { data, isLoading: plansLoading } = usePlans(true);
  const takingLonger = useTakingLonger();
  const creditsPerTypicalNote = useAppSettings()?.creditsPerTypicalNote;

  if (!subscription) {
    return (
      <FullScreenOverlay
        title={
          <Text style={textStyles.title} loading={accountLoading}>
            Setting up your subscription
          </Text>
        }
        subtitle={
          <Text style={textStyles.body} loading={accountLoading}>
            This usually takes a few seconds. This page updates on its own.
          </Text>
        }
        loading={accountLoading}
        loadingLabel="Setting up your subscription"
        view="waiting"
        // The page under it offers Compare plans, so the wait closes only once View subscription is offered.
        onEscape={() => takingLonger && onClose()}
      >
        {/* The shared Loader is viewport-fixed at the top left unless `absolute`; this slot keeps it in the card. */}
        <div className={styles.loaderSlot}>
          <Loader size={40} absolute />
        </div>
        {takingLonger && (
          <>
            <OverlayPanel>
              This is taking longer than usual. Your subscription will appear on Subscription & Billing once it’s confirmed.
            </OverlayPanel>
            <ViewSubscription onClose={onClose} />
          </>
        )}
      </FullScreenOverlay>
    );
  }

  const plan = findSubscriptionPlan(data?.plans, subscription);
  const date = formatBillingDate(subscription.currentPeriodEnd);
  // Only the rows the design shows. While plans load, each plan-dependent text holds its place as a skeleton;
  // it is left out only when plans failed or none matches.
  const trialRow = data && formatTrialAllowanceRow(data, creditsPerTypicalNote);
  const monthlyEnergyRow = plan && formatMonthlyEnergyRow(plan.monthlyCredits, creditsPerTypicalNote);

  return subscription.status === 'trialing' ? (
    <FullScreenOverlay title="Your Trial is ready" view="trial" onEscape={onClose}>
      <OverlayPanel title="Your Trial and next payment">
        <div>{`Selected plan: ${subscription.planName} · ${intervalLabels[subscription.interval]}`}</div>
        {plansLoading ? (
          <div data-testid="trial-allowance-skeleton">
            <TextSkeleton style={textStyles.panelRow} width={skeletonWidths.trialAllowance} />
          </div>
        ) : (
          trialRow && <div>{trialRow}</div>
        )}
        <div>{`First charge: ${date}`}</div>
      </OverlayPanel>
      <OverlayNote>Trial primarily unlocks AI; some assets and actions remain restricted.</OverlayNote>
      <ViewSubscription appearance="clear" onClose={onClose} />
    </FullScreenOverlay>
  ) : (
    <FullScreenOverlay title="Your paid plan is ready" view="paid" onEscape={onClose}>
      <OverlayPanel title="Your plan is active">Monthly Energy is ready.</OverlayPanel>
      <OverlayNote>
        {/* TODO: show the "Paid today" row from the user's latest non-zero subscription payment once firestore.rules lets owners read payments (the $0 Trial-start invoice also writes a row). */}
        <div>{`Next payment: ${formatPrice(subscription.amountCents)} on ${date}`}</div>
        {plansLoading ? (
          <div data-testid="monthly-energy-skeleton">
            <TextSkeleton style={textStyles.detailsRow} width={skeletonWidths.monthlyEnergy} />
          </div>
        ) : (
          monthlyEnergyRow && <div>{monthlyEnergyRow}</div>
        )}
      </OverlayNote>
      <ViewSubscription onClose={onClose} />
    </FullScreenOverlay>
  );
};

const useTakingLonger = () => {
  const [takingLonger, setTakingLonger] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setTakingLonger(true), TAKING_LONGER_MS);
    return () => clearTimeout(timeout);
  }, []);

  return takingLonger;
};

/** Closing reveals Subscription & Billing, which already shows the live account. */
const ViewSubscription = ({ appearance, onClose }: { appearance?: 'clear'; onClose: () => void }) => (
  <Button size="big" fullWidth cornerRadius="smooth" appearance={appearance} onClick={onClose}>
    View subscription
  </Button>
);

const textStyles = {
  title: { lineHeight: 42, textHeight: 20, charWidth: 13.4 },
  body: { lineHeight: 21, textHeight: 10, charWidth: 6.8 },
  panelRow: { lineHeight: 21, textHeight: 10 },
  detailsRow: { lineHeight: 18, textHeight: 8 },
} satisfies Record<string, TextStyle | SkeletonLineStyle>;

// Loading widths of the data-dependent texts, measured in Inter at their text styles: the Trial row, with its
// typical-note estimate, renders 409; the Monthly Energy row, with its estimate, 312.
const skeletonWidths = {
  trialAllowance: 409,
  monthlyEnergy: 312,
};
